import { Link, Navigate } from '@tanstack/react-router'
import type { DealerStatus } from '@tex/finance-shared'
import { type FormEvent, useState } from 'react'
import { Alert, Button, Card, Field, Input, Select, StatusBadge, Textarea } from '../components/ui'
import { errorMessage } from '../lib/api'
import { formatDate } from '../lib/format'
import { useCreateDealer, useDealers } from '../lib/dealers'
import { isTexStaff, useMe } from '../lib/session'

const STATUSES: DealerStatus[] = ['pending_review', 'active', 'suspended']

export function DealersListPage() {
  const me = useMe()
  const [status, setStatus] = useState<DealerStatus | undefined>(undefined)
  const dealers = useDealers(status)

  if (me.data === null) return <Navigate to="/sign-in" />
  if (me.data && !isTexStaff(me.data)) return <Alert tone="error">Only Tex staff can see the dealer list.</Alert>

  return (
    <section className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-neutral-900">Dealers</h1>
          <p className="mt-1 text-sm text-neutral-500">
            New dealers start in <em>pending review</em>; only active dealers can create cases or appear publicly.
          </p>
        </div>
        <Field label="Status">
          <Select value={status ?? ''} onChange={(e) => setStatus((e.target.value || undefined) as DealerStatus | undefined)}>
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replace('_', ' ')}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {dealers.isError && <Alert tone="error">{errorMessage(dealers.error)}</Alert>}
      <div className="overflow-hidden rounded-xl border border-[var(--border-subtle)] bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-neutral-100 bg-neutral-50/60 text-left font-display text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-2">Dealer</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">Members</th>
              <th className="px-4 py-2 text-right">Cases</th>
              <th className="px-4 py-2">Created</th>
            </tr>
          </thead>
          <tbody>
            {dealers.data?.dealers.map((d) => (
              <tr key={d.id} className="border-t border-neutral-100 transition-colors hover:bg-neutral-50/60">
                <td className="px-4 py-2">
                  <Link to="/dealers/$dealerId" params={{ dealerId: d.id }} className="font-medium hover:underline">
                    {d.trading_name ?? d.legal_name}
                  </Link>
                  <div className="text-xs text-neutral-500">
                    {d.legal_name} · /{d.slug}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <StatusBadge status={d.status} />
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{d.member_count}</td>
                <td className="px-4 py-2 text-right tabular-nums">{d.case_count}</td>
                <td className="px-4 py-2 text-neutral-500">{formatDate(d.created_at)}</td>
              </tr>
            ))}
            {dealers.data && dealers.data.dealers.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-neutral-500">
                  No dealers{status ? ` with status ${status.replace('_', ' ')}` : ''}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {me.data?.tex_role === 'ops_admin' && <CreateDealerCard />}
    </section>
  )
}

function CreateDealerCard() {
  const create = useCreateDealer()
  const [legalName, setLegalName] = useState('')
  const [tradingName, setTradingName] = useState('')
  const [website, setWebsite] = useState('')
  const [notes, setNotes] = useState('')

  function submit(e: FormEvent) {
    e.preventDefault()
    create.mutate(
      {
        legal_name: legalName.trim(),
        trading_name: tradingName.trim() || undefined,
        website: website.trim() || undefined,
        reviewer_notes: notes.trim() || undefined,
      },
      {
        onSuccess: () => {
          setLegalName('')
          setTradingName('')
          setWebsite('')
          setNotes('')
        },
      },
    )
  }

  return (
    <Card title="Add a dealer">
      <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
        <Field label="Legal name">
          <Input required minLength={2} value={legalName} onChange={(e) => setLegalName(e.target.value)} />
        </Field>
        <Field label="Trading name" hint="Shown to borrowers; also seeds the public slug.">
          <Input value={tradingName} onChange={(e) => setTradingName(e.target.value)} />
        </Field>
        <Field label="Website">
          <Input type="url" placeholder="https://" value={website} onChange={(e) => setWebsite(e.target.value)} />
        </Field>
        <Field label="Reviewer notes" hint="Internal; never shown to the dealer.">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" disabled={create.isPending}>
            Create in pending review
          </Button>
          {create.isError && <Alert tone="error">{errorMessage(create.error)}</Alert>}
          {create.isSuccess && (
            <Alert tone="success">
              Created.{' '}
              <Link to="/dealers/$dealerId" params={{ dealerId: create.data.id }} className="underline">
                Open {create.data.brand.display_name}
              </Link>{' '}
              to verify and invite the owner.
            </Alert>
          )}
        </div>
      </form>
    </Card>
  )
}
